/**
 * digitAnatomy — ADR-023 §3: **the medical facts, in one file, with their
 * sources.** Which bones a digit has, what its joints are called, and how its
 * length divides between them.
 *
 * ONE responsibility: answer "what IS this digit". It measures nothing, places
 * nothing, and knows about no specimen. `place-digits` asks it what it is
 * building before it builds it.
 *
 * WHY THIS EXISTS AS A FILE. These facts were spread across three places that
 * could not disagree out loud: a `JOINT_FRACTIONS` constant inside a placement
 * module, a `jointNames` tuple beside it, and prose in a design document. The
 * cost of that was not tidiness — it is that **the thumb was built as a finger**
 * for as long as the graft has existed, because nothing in the code ever said
 * it was not one. A hand's anatomy is a fact about hands, not an implementation
 * detail of one function.
 *
 * ── THE TWO BONE SETS ───────────────────────────────────────────────────────
 *
 * A rigged digit is four nodes (`…Digit1..4`) — three bones and a leaf tip
 * marker. The nodes are the same count for every digit and **they mean
 * different things**, which is the confusion this file ends:
 *
 *   finger   proximal phalanx → intermediate → distal → tip
 *            joints:  MCP  ·  PIP  ·  DIP
 *            (the metacarpal is NOT rigged: it lies in the palm and does not
 *             articulate independently, so a finger's first node is its knuckle)
 *
 *   thumb    METACARPAL → proximal phalanx → distal → tip
 *            joints:  CMC  ·  MCP  ·  IP
 *            (the thumb has no intermediate phalanx — it is the one digit with
 *             two phalanges — and its metacarpal IS rigged, so its first node
 *             sits at the WRIST, not at the knuckle row)
 *
 * Allen, from the field: *"thumbs have two super important deviations… no
 * middle phalanges bone segment… and the thumb metacarpal is significantly
 * shorter."* Both true. The bone COUNT was already right; what was wrong is
 * that every digit was placed with finger semantics, so `Thumb1` landed on the
 * finger knuckle arc instead of at the CMC — measured at 50–96% of the way to
 * the tip when it belongs at ~0 (ADR-023 §1.5).
 *
 * ── THE LADDERS ────────────────────────────────────────────────────────────
 *
 * Fractions are **cumulative arclengths along the digit's own span**, from its
 * base joint to its tip. The base is a different landmark per digit
 * ({@link DigitAnatomy.baseLandmark}), which is exactly the thing that was
 * being got wrong.
 *
 *   fingers  φ² : φ : 1  ⇒  [0, 0.500, 0.809, 1]
 *            The golden-ratio ladder, ratified 2026-07-27 (HAND_STUDIO_KNUCKLES)
 *            and kept by ADR-023 D1 as a PRIOR the volume may correct — it
 *            proposes an arclength; the tube decides the position.
 *
 *   thumb    45 : 32 : 23  ⇒  [0, 0.45, 0.77, 1]
 *            Anthropometric (ADR-023 D2, ruled by Allen 2026-08-13): the
 *            metacarpal is the longest segment, then proximal, then distal.
 *            Close to φ's 50:31:19 and deliberately not it — the thumb is a
 *            different bone set and says so.
 *
 * Both are PRIORS, not laws (ADR-010 §11's standing), and both are corrected
 * within D1's bound by what the volume actually shows.
 *
 * Depends on: hands/propose-digit-names (the digit vocabulary) — nothing else,
 * by design. Depended on by: hands/place-digits.
 */
import type { DigitName } from './propose-digit-names.js';

/**
 * Every joint name in the hand's vocabulary.
 *
 * A UNION, DELIBERATELY (ADR-023 §6): widening this is what makes the compiler
 * enumerate every site that must now say which digit it is talking about. A
 * consumer that assumes `mcp | pip | dip` does not build once `cmc` and `ip`
 * exist, which is the enforcement for D3's rename.
 */
export type DigitJoint = 'cmc' | 'mcp' | 'pip' | 'ip' | 'dip' | 'tip';

/** What a digit's first joint is anchored to — the landmark placement must find. */
export type BaseLandmark =
  /** The knuckle row: an arc at a common distance from the wrist (fingers). */
  | 'knuckle-row'
  /** The wrist: the CMC sits at the base of the thumb's own tube (thumb only). */
  | 'wrist';

export interface DigitAnatomy {
  readonly digit: DigitName;
  /** Joint names for nodes 1–4, base→tip. Node 4 is the leaf tip marker. */
  readonly joints: readonly [DigitJoint, DigitJoint, DigitJoint, DigitJoint];
  /** The three bones between them, base→tip, by their anatomical names. */
  readonly bones: readonly [string, string, string];
  /**
   * Cumulative arclength fractions of the digit's OWN span, base→tip. Always
   * begins at 0 and ends at 1; the two interior values are the ladder.
   */
  readonly fractions: readonly [number, number, number, number];
  /** Where node 1 belongs. Fingers start at the knuckle; the thumb at the wrist. */
  readonly baseLandmark: BaseLandmark;
}

const PHI = (1 + Math.sqrt(5)) / 2;
/**
 * The finger ladder, DERIVED rather than typed as decimals so the constant
 * cannot drift from the construction it claims to be: segments φ² : φ : 1,
 * normalized to a cumulative [0, …, 1].
 */
const FINGER_FRACTIONS: readonly [number, number, number, number] = (() => {
  const segments = [PHI * PHI, PHI, 1];
  const total = segments.reduce((a, b) => a + b, 0);
  const a = segments[0]! / total;
  const b = (segments[0]! + segments[1]!) / total;
  return [0, a, b, 1];
})();

/**
 * The thumb ladder — metacarpal : proximal : distal = 45 : 32 : 23 (D2).
 * Written as the SEGMENTS it is quoted in, and accumulated here, so the number
 * a reader checks against a source is the number in the file.
 */
const THUMB_SEGMENTS: readonly [number, number, number] = [45, 32, 23];
const THUMB_FRACTIONS: readonly [number, number, number, number] = (() => {
  const total = THUMB_SEGMENTS.reduce((a, b) => a + b, 0);
  const a = THUMB_SEGMENTS[0]! / total;
  const b = (THUMB_SEGMENTS[0]! + THUMB_SEGMENTS[1]!) / total;
  return [0, a, b, 1];
})();

const FINGER: Omit<DigitAnatomy, 'digit'> = {
  joints: ['mcp', 'pip', 'dip', 'tip'],
  bones: ['proximal phalanx', 'intermediate phalanx', 'distal phalanx'],
  fractions: FINGER_FRACTIONS,
  baseLandmark: 'knuckle-row',
};

const THUMB: Omit<DigitAnatomy, 'digit'> = {
  joints: ['cmc', 'mcp', 'ip', 'tip'],
  bones: ['metacarpal', 'proximal phalanx', 'distal phalanx'],
  fractions: THUMB_FRACTIONS,
  baseLandmark: 'wrist',
};

/** What this digit is. The thumb is not a finger; everything else is. */
export function digitAnatomy(digit: DigitName): DigitAnatomy {
  return digit === 'Thumb' ? { digit, ...THUMB } : { digit, ...FINGER };
}

/**
 * The joints that ARTICULATE — nodes 1–3, the ones that get a frame and a bone.
 * Node 4 is a leaf marker: the ontology rejects a joint frame for it, and the
 * distal bone already covers that flesh.
 */
export function articulatingJoints(digit: DigitName): readonly [DigitJoint, DigitJoint, DigitJoint] {
  const [a, b, c] = digitAnatomy(digit).joints;
  return [a, b, c];
}
